import React, { CSSProperties } from 'react';
import { Box, SelectChangeEvent } from '@mui/material';
import { DashboardFilters } from 'src/utils/constant';
import DateRangePicker from 'src/components/daterangepicker/DateRangePicker';
import { TextField } from 'src/components/new/inputs/TextField';
import SearchIcon from '@mui/icons-material/Search';
import BkToolTip from 'src/components/tooltip/BkToolTip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Select from 'src/components/new/inputs/Select';
import ServiceBadge from 'src/components/service-badge/ServiceBadge';

interface JobHistoryFiltersProps {
  noticeTypes: { id: string; label: string }[];
  statuses: { id: string; label: string }[];
  orgTypes: { id: string; label: string }[];
  onHoldReasons: { id: string; label: string }[];
  serviceOptions: { id: string; label: string }[];
  selectedFilters: DashboardFilters;
  onFilterChange: (event: SelectChangeEvent<string | (string | null)[]>) => void;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchTerm: string;
}

const wrapperDivStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  gap: '1rem',
  width: '100%',
  // Keeps the box from being squeezed narrower than its contents, which would
  // overflow it and let the action buttons sit on top of the last filter.
  minWidth: 'max-content',
};

interface FilterConfigItem {
  id: string;
  data: { id: string; label: string }[];
  label: string;
  placeholder: string;
  width: string;
  name: string;
  multiple: boolean;
  /** Optional custom renderer for the dropdown option content. */
  renderOptionLabel?: (item: { id: string; label: string }) => React.ReactNode;
}

const JobHistoryFilters: React.FC<JobHistoryFiltersProps> = ({
  noticeTypes,
  statuses,
  orgTypes,
  onHoldReasons,
  serviceOptions,
  selectedFilters,
  onFilterChange,
  handleSearchChange,
  searchTerm,
}) => {
  const filterConfig: FilterConfigItem[] = [
    {
      id: 'status',
      data: statuses,
      label: 'Status',
      placeholder: 'Choose',
      width: '10.625rem',
      name: 'status',
      multiple: true,
    },
    {
      id: 'organization.type',
      data: orgTypes,
      label: 'Organization type',
      placeholder: 'Choose',
      width: '10.625rem',
      name: 'organization.type',
      multiple: false,
    },
    {
      id: 'type',
      data: noticeTypes,
      label: 'Notice Type',
      placeholder: 'Choose',
      width: '10.625rem',
      name: 'type',
      multiple: false,
    },
    {
      id: 'onHoldReason',
      data: onHoldReasons,
      label: 'On Hold Reason',
      placeholder: 'Choose',
      width: '10.625rem',
      name: 'onHoldReason',
      multiple: true,
    },
    {
      id: 'service',
      data: serviceOptions,
      label: 'Job Service',
      placeholder: 'Choose',
      width: '10.625rem',
      name: 'service',
      multiple: true,
      renderOptionLabel: (item) => <ServiceBadge service={item.id} />,
    },
  ];
  return (
    <Box sx={wrapperDivStyles}>
      <Box sx={{ width: '10.625rem', flexShrink: 0 }}>
        <TextField
          value={searchTerm}
          onChange={handleSearchChange}
          name="search"
          id="search"
          removeSpacer
          label={
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem', whiteSpace: 'nowrap' }}>
              Job Search
              <BkToolTip Icon={InfoOutlinedIcon} title="Search by Job Number, Organization, User, and Case Number" />
            </Box>
          }
          error=""
          endAdornment={<SearchIcon sx={{ color: '#6D7176', marginLeft: '-2rem' }} />}
        />
      </Box>
      <Box sx={{ minWidth: '9.375rem' }}>
        <DateRangePicker
          value={[selectedFilters.createdAt_start, selectedFilters.createdAt_end].map((date) =>
            date ? new Date(date) : null,
          )}
          onChange={onFilterChange}
        />
      </Box>
      {filterConfig.map(({ id, data, width, label, placeholder, name, multiple, renderOptionLabel }) => (
        <Box key={id} style={{ display: 'flex', flexDirection: 'column' }}>
          <Select
            label={label}
            key={id}
            data={data}
            name={name}
            onChange={onFilterChange}
            value={selectedFilters[id as keyof typeof selectedFilters] ?? ''}
            error=""
            disabled={false}
            width={width}
            multiple={multiple}
            placeholder={placeholder}
            renderOptionLabel={renderOptionLabel}
          />
        </Box>
      ))}
    </Box>
  );
};

export default JobHistoryFilters;
